import { Spin } from 'antd';
import download from 'downloadjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AiOutlineDownload } from 'react-icons/ai';
import { GrFormView } from 'react-icons/gr';
import Apis from '../../../../../api';
import HSModal from '../../../../common/Modal';
import PDFView from './PDFView';

type Props = {
    docUUID: string;
    bookingUUID?: string;
    dealUUID?: string;
    rootUUID?: string;
    fileName: string;
    group?: string;
    pageType: 'reportingObligation' | 'updateOrExecuteAgreement' | 'dataRoom' | 'invetmentDashboard';
    list?: any[];
    isDownload?: boolean;
};

const FileView: React.FC<Props> = ({
    fileName,
    docUUID,
    pageType,
    bookingUUID,
    dealUUID,
    rootUUID,
    list,
    group,
    isDownload,
}) => {
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [uuid, setUUID] = useState<string | undefined>(undefined);
    const [downloadFile, setDownloadFile] = useState<boolean>(false);
    list = (list || []).filter((l) => (l.name || l.title || '').match('jpg|jpeg|png|gif|pdf'));

    const type = useMemo(() => {
        if (fileName) {
            return fileName.substring(fileName.lastIndexOf('.') + 1);
        }
        return '';
    }, [fileName]);

    const activeFileExtention = useMemo(() => {
        if (!list) {
            return undefined;
        }
        const data = list.find((d) => d.uuid === uuid);
        return (data?.name || data?.title || '').substring((data?.name || data?.title || '').lastIndexOf('.') + 1);
    }, [uuid, list]);

    const name = useMemo(() => {
        if (!list) {
            return undefined;
        }
        const data = list.find((d) => d.uuid === uuid);
        return data?.name || data?.title || '';
    }, [uuid, list]);

    const index = useMemo(() => {
        if (list && open && uuid) {
            return list.findIndex((l) => l.uuid === uuid);
        }
        return -1;
    }, [list, open, uuid]);

    const onOpen = () => {
        setUUID(docUUID);
        setOpen(true);
    };

    const onClose = () => {
        setUUID(undefined);
        setOpen(false);
        setFile(undefined);
        setLoading(false);
        setDownloadFile(false);
    };
    const handleLoadFile = useCallback(
        (isDownload: boolean) => {
            if (pageType === 'invetmentDashboard') {
                if (bookingUUID) {
                    setLoading(true);
                    Apis.downloadExecuteOrUpdateDoc(bookingUUID, docUUID)
                        .then((res) => {
                            let file = res.data;
                            if (isDownload) {
                                download(file, name);
                                onClose();
                            } else {
                                setFile(file);
                            }
                        })

                        .finally(() => {
                            setLoading(false);
                        });
                } else if (dealUUID) {
                    setLoading(true);
                    Apis.downloadDataRoomDocument(dealUUID, rootUUID || docUUID, docUUID, 'ExecutedAgreements')
                        .then((res) => {
                            let file = res.data;
                            if (isDownload) {
                                download(file, name);
                                onClose();
                            } else {
                                setFile(file);
                            }
                        })
                        .finally(() => {
                            setLoading(false);
                        });
                }
            }
            if (pageType === 'dataRoom' && dealUUID && rootUUID && docUUID && group) {
                setLoading(true);
                Apis.downloadDataRoomDocument(dealUUID, rootUUID, docUUID, group)
                    .then((res) => {
                        let file = res.data;
                        if (isDownload) {
                            download(file, name);
                            onClose();
                        } else {
                            setFile(file);
                        }
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        },
        [dealUUID, rootUUID, docUUID, pageType, bookingUUID, group, name],
    );

    useEffect(() => {
        try {
            if (open) {
                setFile(null);
                handleLoadFile(false);
            } else if (downloadFile) {
                setFile(null);
                handleLoadFile(true);
            }
        } catch (err) {
            setLoading(false);
        }
    }, [open, downloadFile, handleLoadFile]);

    const nextOrPrevDoc = useMemo(() => {
        if (!list) {
            return null;
        }
        const fetchDoc = (uuid: string) => {
            setUUID(uuid);
            setFile(null);

            if (pageType === 'invetmentDashboard') {
                const row = list?.find((i) => i.uuid === uuid);
                if (row && row.bookingUUID) {
                    setLoading(true);
                    Apis.downloadExecuteOrUpdateDoc(row.bookingUUID, uuid)
                        .then((res) => {
                            let file = res.data;
                            setFile(file);
                        })
                        .finally(() => {
                            setLoading(false);
                        });
                } else if (row && row.dealUUID) {
                    setLoading(true);
                    Apis.downloadDataRoomDocument(
                        row.dealUUID,
                        row.dealDocParentUUID || uuid,
                        uuid,
                        'ExecutedAgreements',
                    )
                        .then((res) => {
                            let file = res.data;
                            setFile(file);
                        })
                        .finally(() => {
                            setLoading(false);
                        });
                }
            }

            if (pageType === 'dataRoom') {
                const row = list?.find((i) => i.uuid === uuid);
                if (row.dealUUID && row.uuid && row.group && (row.parentUUID || row.uuid)) setLoading(true);
                Apis.downloadDataRoomDocument(row.dealUUID, row.parentUUID || row.uuid, row.uuid, row.group)
                    .then((res) => {
                        let file = res.data;
                        setFile(file);
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        };
        return (
            <div>
                {index !== -1 && !loading && (
                    <div className="flex justify-between items-center">
                        {index !== 0 ? (
                            <div className="cursor-pointer flex flex-col">
                                {list && list[index - 1] && (
                                    <p
                                        className="text-blue-400 text-xs"
                                        onClick={() => fetchDoc(list && list[index - 1].uuid)}
                                    >
                                        {list[index - 1].title || list[index - 1].name}{' '}
                                        <span className="text-black">(Prev Doc)</span>
                                    </p>
                                )}
                            </div>
                        ) : (
                            <div />
                        )}
                        {index !== list.length ? (
                            <div className="cursor-pointer flex flex-col">
                                {list && list[index + 1] && (
                                    <p
                                        className="text-blue-400 text-xs"
                                        onClick={() => fetchDoc(list && list[index + 1].uuid)}
                                    >
                                        {list && list[index + 1] && (list[index + 1].title || list[index + 1].name)}{' '}
                                        <span className="text-black">(Next Doc)</span>
                                    </p>
                                )}
                            </div>
                        ) : (
                            <div />
                        )}
                    </div>
                )}
            </div>
        );
    }, [list, index, loading, pageType]);

    return (
        <div>
            <div className="flex gap-1 justify-between items-center">
                {type.match('jpg|jpeg|png|gif|pdf') && (
                    <GrFormView
                        className="cursor-pointer"
                        size={pageType === 'dataRoom' ? '1.4rem' : '1.2rem'}
                        onClick={onOpen}
                        color="#13974B"
                    />
                )}
                {isDownload && !open && !loading ? (
                    <AiOutlineDownload
                        onClick={() => {
                            setDownloadFile(true);
                            setUUID(docUUID);
                        }}
                        size={pageType === 'dataRoom' ? '1.4rem' : '1.2rem'}
                        color="#13974B"
                    />
                ) : null}
                {loading ? <Spin /> : null}
            </div>

            <HSModal title={name} open={open} setOpen={onClose} width="1080px" maskClosable={true} className="relative">
                <div className="mb-2">{nextOrPrevDoc}</div>
                <div
                    className={`${
                        pageType === 'reportingObligation' ? 'mb-16' : ''
                    } max-h-[74vh] h-full overflow-y-auto`}
                >
                    {type && (
                        <div className="w-[99%]">
                            {activeFileExtention === 'pdf' && <PDFView loading={loading} file={file} />}
                            {activeFileExtention.match('jpg|jpeg|png|gif') && file && (
                                <img src={window.URL.createObjectURL(new Blob([file]))} alt="viewImage" />
                            )}
                        </div>
                    )}
                </div>
            </HSModal>
        </div>
    );
};

export default FileView;
